import React, { useState, useEffect } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import "./Result.css";
import ResultModalLayout from '@/components/ResultModalLayout';

// Helper function to map card string to image path
const getCardImage = (cardString) => {
  if (!cardString) return null;
  // Adjust path to match where images are stored in Admin
  return `/assets/img/cards/${cardString}.png`;
};

// Helper function to parse card string
const parseCards = (cardString) => {
  if (!cardString) return [];

  const cards = cardString.split(',');
  return cards.map(card => getCardImage(card.trim()));
};

// Helper function to parse rdesc
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};

  const parts = rdesc.split('#');
  return {
    total: parts[0] || "",
    nextTotal: parts[1] || "",
    calculation: parts[2] || "",
    result: parts[3] || "",
    evenOdd: parts[4] || "",
    redBlack: parts[5] || "",
  };
};

const ResultDum10 = ({
  data = [],
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('dum10');
        if (response?.data?.data?.res) {
          // Map win values: "1" = "Y" (Yes), "2" = "N" (No)
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "Y" : item.win === "2" ? "N" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
  }, []);

  const handleResultClick = async (result, index) => {
    setSelectedResult(result);
    setLoading(true);

    // First, ensure we have last results
    if (lastResults.length === 0) {
      try {
        const response = await getLastResults('dum10');
        if (response?.data?.data?.res) {
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "Y" : item.win === "2" ? "N" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    }

    // Find the mid for this result
    let mid = null;
    if (index < lastResults.length) {
      mid = lastResults[index]?.mid;
    } else if (index < data.length + (gameData?.lrs?.length || 0)) {
      mid = gameData?.data?.data?.mid || gameData?.mid;
    }

    // Fetch detail results if we have a mid
    if (mid) {
      try {
        const response = await getDetailResults('dum10', mid.toString());
        if (response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }

    setLoading(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data based on selected result
  const getModalData = () => {
    if (!selectedResult) return null;

    // Use detailResult if available
    const cards = detailResult?.card ? parseCards(detailResult.card) : [];
    const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : {};
    const winner = detailResult?.winnat || (selectedResult === "Y" ? "Yes" : selectedResult === "N" ? "No" : "");

    // Determine winner from API data
    const winValue = detailResult?.win;
    const isYesWinner = winValue === "1" || winner === "Yes" || selectedResult === "Y";
    const isNoWinner = winValue === "2" || winner === "No" || selectedResult === "N";

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.mid || "",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      eventName: detailResult?.ename || "Dus ka Dum",
      cards: cards,
      winner: winner,
      isYesWinner: isYesWinner,
      isNoWinner: isNoWinner,
      resultDetails: {
        result: rdescData.result || winner,
      },
    };
  };

  const modalData = getModalData();

  // Combine API results with fallback data
  // Map win values: "1" = "Y", "2" = "N"
  const allResults = lastResults.length > 0
    ? lastResults.map(r => r.win)
    : (gameData?.data?.lrs || []).map(win => win === "1" ? "Y" : win === "2" ? "N" : win);

  return (
    <>
      <div className="result-row">
        {allResults.map((res, index) => (
          <div
            key={index}
            className={`result-circle ${res === "Y" ? "yes" : "no"}`}
            onClick={() => handleResultClick(res, index)}
            style={{ cursor: 'pointer' }}
          >
            {res}
          </div>
        ))}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title={modalData.eventName || "Dus ka Dum Result"}
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div>
            {/* Round ID */}
            <h6 className="text-right round-id" style={{ marginBottom: '1rem' }}>
              <b>Round Id:</b> {modalData.roundId}
            </h6>

            {/* Cards Display Section - Horizontal Scrollable */}
            <div className="row mt-2">
              <div className="col-12">
                <div
                  className="text-center"
                  style={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: '8px',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  {modalData.cards?.map((card, index) => (
                    card && (
                      <div key={index} style={{ position: 'relative', minWidth: '30px' }}>
                        <img
                          src={card}
                          alt={`Card ${index + 1}`}
                          onError={(e) => {
                            const cardName = card.split('/').pop();
                            const fallbackPath = `/assets/img/card-results/${cardName}`;
                            if (e.target.src !== fallbackPath) {
                              e.target.src = fallbackPath;
                            } else {
                              e.target.style.display = 'none';
                            }
                          }}
                          style={{
                            maxWidth: '30px',
                            borderRadius: '4px'
                          }}
                        />
                      </div>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultDum10;

